// react-table is extending from object, so we have to do that as well
/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import { Row } from 'react-table';
import { useDeepCompareMemo, useDeepCompareCallback } from 'use-deep-compare';

import { CustomGroupedRow, CustomSubRow } from './components';
import { useGapAnalysisTools } from '../../../../../../../../../hooks';

interface GroupedRowsProps {
    /**
     * Tailwind size to apply to text for grouped rows
     * @defaultValue 'sm'
     */
    groupTextSize?: 'xs' | 'sm' | 'base' | 'lg';
    /**
     * The `flatRows` array returned by the react-table `useTable()` effect
     */
    flatRows: Row<TrqkGapChannelAudio>[];
    /**
     * Outer row height
     */
    rowHeight: number;
    /**
     * Sub-row height
     */
    subRowHeight: number;
    /**
     * The `prepareRow()` function returned by the react-table `useTable()` effect
     */
    prepareRow(row: Row<TrqkGapChannelAudio>): void;
    /**
     * Tailwind size to apply to text
     */
    textSize?: 'xs' | 'sm' | 'base' | 'lg';
}

/**
 * Renders table rows as grouped rows that are expandable. Each grouped row has a caret indicating
 * whether it is expanded or not.
 *
 * @remarks
 * To use this component, the react-table `useTable` hook must be passed the `useExpanded` and
 * `useGroupBy` hooks. Not intended for use within a render function for <FixedSizeList> from
 * react-window.
 * @param props - {@link GroupedRowsProps}
 */
export function CustomGroupedRows({
    groupTextSize = 'sm',
    flatRows,
    rowHeight,
    prepareRow,
    subRowHeight,
    textSize = 'sm',
}: GroupedRowsProps): JSX.Element {
    const { context } = useGapAnalysisTools();
    const currentChannelId = context?.currentChannelId ?? null;
    const audioEdits = currentChannelId
        ? context.edits?.[currentChannelId]?.audioEdits ?? null
        : null;

    const expandingRows = useDeepCompareMemo(() => {
        let offset = 0;

        return flatRows.reduce(
            (
                acc: ExpandingRow<TrqkGapChannelAudio>[],
                row,
                index,
            ): ExpandingRow<TrqkGapChannelAudio>[] => {
                if (row.groupByID !== undefined) {
                    // row.groupByID is only defined for expanding rows. We need to keep track of the
                    // original row index here so we can correctly specify aria-rowindex within
                    // sub-rows.
                    acc.push({
                        row,
                        index: index + offset,
                    });

                    offset += 1;
                }

                return acc;
            },
            [],
        );
    }, [flatRows]);

    const getMatchedWorkIdWasEdited = useDeepCompareCallback(
        (gapAudioId: Maybe<string>): boolean => {
            const edits =
                gapAudioId === null ? null : audioEdits?.[gapAudioId] ?? null;

            return edits?.edited.songId !== undefined;
        },
        [audioEdits],
    );

    return (
        <>
            {expandingRows.map(
                ({
                    row,
                    index,
                }: ExpandingRow<TrqkGapChannelAudio>): JSX.Element => {
                    prepareRow(row);
                    const { key: rowKey } = row.getRowProps();
                    const ariaRowIndex = index + 1;

                    const rowGapAudioId: Maybe<string> = row.isExpanded
                        ? null
                        : row?.values?.includeAudioFile?.gapAudioId ?? null;

                    return (
                        <div key={rowKey}>
                            <CustomGroupedRow
                                ariaRowIndex={ariaRowIndex}
                                groupTextSize={groupTextSize}
                                matchedWorkIdWasEdited={getMatchedWorkIdWasEdited(
                                    rowGapAudioId,
                                )}
                                height={rowHeight}
                                row={row}
                            />
                            {row.isExpanded &&
                                row.subRows.map((subRow, subRowIndex) => {
                                    prepareRow(subRow);

                                    const { key: subRowKey } =
                                        subRow.getRowProps();

                                    const subRowGapAudioId = subRow?.id ?? null;

                                    return (
                                        <span key={subRowKey}>
                                            <CustomSubRow
                                                ariaRowIndex={
                                                    ariaRowIndex +
                                                    subRowIndex +
                                                    2
                                                }
                                                height={subRowHeight}
                                                matchedWorkIdWasEdited={getMatchedWorkIdWasEdited(
                                                    subRowGapAudioId,
                                                )}
                                                row={subRow}
                                                textSize={textSize}
                                            />
                                        </span>
                                    );
                                })}
                        </div>
                    );
                },
            )}
        </>
    );
}
